import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import { DateRange } from '@domain/value-objects/DateRange';
import { cx } from '../design-system';
import { MONTHS_LONG, WEEKDAYS_SHORT } from '../format';
import styles from './RangePicker.module.css';

/**
 * A month grid that picks a range by two clicks or one drag.
 *
 * Both gestures exist because both are natural: a two-week block is easier to
 * drag, a single day easier to click. Keyboard works too — the cells are real
 * buttons, so Tab and Enter select without any extra handling.
 *
 * Weekends and holidays render dimmed but stay selectable. A range legitimately
 * spans them; they simply do not count as business days, and the count below
 * the grid is what says so.
 */

export interface RangePickerProps {
  readonly value: DateRange | null;
  readonly onChange: (range: DateRange | null) => void;
  /** Earliest selectable day. Days before it render disabled. */
  readonly minimum: CalendarDate;
  /**
   * Month to open on. Defaults to the earliest selectable one, which is right
   * for a vacation request but wrong for a medical leave — that allows months
   * of backdating, and opening half a year in the past would make the person
   * page forward to reach today.
   */
  readonly initialMonth?: CalendarDate;
  /** ISO dates that are public holidays. */
  readonly holidays: ReadonlySet<string>;
  /** ISO dates where someone from the same sub area is already away. */
  readonly busy: ReadonlyMap<string, string>;
}

/**
 * El rango que sale de un gesto, recortado al primer día reservable.
 *
 * **Un botón `disabled` no basta**, y esto se midió: una celda deshabilitada no
 * recibe clics, pero **sí recibe `pointerenter`**. Arrastrar desde un día válido
 * hacia atrás extendía la selección sobre los días grises, y el asistente acababa
 * enseñando «estás avisando con -22 días» — un aviso previo en negativo, que no es
 * un aviso sino un imposible con cara de advertencia.
 *
 * Aquí pasan todas las selecciones del calendario, así que es el sitio donde el
 * suelo se cumple una sola vez en lugar de en cada gesto.
 *
 * Se recorta y no se descarta: quien arrastra hacia atrás pide «desde lo más
 * pronto posible», y darle el rango legal más cercano es contestarle. Descartar
 * el gesto entero se leería como que el calendario se atascó.
 */
export const rangeFrom = (
  from: CalendarDate,
  to: CalendarDate,
  minimum: CalendarDate,
): DateRange | null => {
  const [rawStart, rawEnd] = from.isAfter(to) ? [to, from] : [from, to];
  const start = rawStart.isBefore(minimum) ? minimum : rawStart;
  const end = rawEnd.isBefore(minimum) ? minimum : rawEnd;
  const range = DateRange.create(start, end);
  return range.ok ? range.value : null;
};

export const RangePicker = ({
  value,
  onChange,
  minimum,
  initialMonth,
  holidays,
  busy,
}: RangePickerProps): ReactNode => {
  const [anchor, setAnchor] = useState(() => value?.start ?? initialMonth ?? minimum);

  /*
   * The pending first click, in a ref rather than state.
   *
   * Two pointer events can land in the same React batch — a fast double tap on
   * touch does it — and a state read would still see `null` on the second,
   * restarting the range instead of completing it. A ref is read at the moment
   * the handler runs, which is what a pointer gesture needs.
   */
  /**
   * El día que lleva el foco del teclado.
   *
   * Una rejilla de fechas es **una sola parada de tabulador**, no treinta y una:
   * el patrón es entrar con Tab y moverse con las flechas. Antes cada celda era
   * su propia parada, así que cruzar un mes eran treinta pulsaciones de Tab —y en
   * Safari de macOS, donde el tabulador no se detiene en los botones salvo que se
   * active a mano, ninguna—. El texto prometía «Tab para moverte» y quien lo
   * intentaba encontraba que la promesa era, en el mejor caso, agotadora.
   *
   * `null` hasta que alguien usa el teclado: mientras tanto la parada es el
   * primer día seleccionable, para no robarle el orden natural a la página.
   */
  const [focusedDay, setFocusedDay] = useState<CalendarDate | null>(null);
  /*
   * El mismo día, en un ref, por lo mismo que `dragFrom`.
   *
   * Dejar la tecla pulsada dispara repeticiones que pueden caer en el mismo lote
   * de React, y una lectura de estado seguiría viendo el día anterior: la segunda
   * flecha no movería nada. Se midió con dos flechas seguidas — avanzaba un día,
   * no dos.
   */
  const focusedRef = useRef<CalendarDate | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const dragFrom = useRef<CalendarDate | null>(null);
  /**
   * Whether the pointer has entered a different cell since it went down.
   *
   * This is what actually separates the two gestures: a drag sweeps across
   * cells, a click does not. Comparing event targets on release looked
   * equivalent and was not — it cleared the pending anchor on an ordinary
   * click, so the second click started a new range instead of closing the
   * first, and every two-click pick collapsed to a single day.
   */
  const swept = useRef(false);

  const month = DateRange.month(anchor);
  const days = month.toDayArray();

  // Sunday-first, matching the weekday header the design uses.
  const leadingBlanks = month.start.dayOfWeek;

  /**
   * El único sitio por el que sale una selección — y por eso, donde se corta.
   *
   * Las celdas de días pasados llevan `disabled` desde siempre, y aun así se
   * reportó una selección que empezaba veintidós días antes de hoy: el aviso
   * previo salía en **negativo**, que no es un aviso sino un imposible. Un
   * atributo en el botón sólo cubre los caminos que pasan por el botón — el
   * arrastre, un rango que llega ya hecho al reagendar, un atajo mal calculado— y
   * este cierre los cubre todos.
   *
   * Se recorta en vez de descartarse: quien arrastra del 26 hacia atrás está
   * pidiendo «desde lo más pronto posible», y darle el rango legal más cercano es
   * contestarle. Descartar el gesto entero se leería como que el calendario se
   * atascó.
   */
  const commit = (from: CalendarDate, to: CalendarDate): void => {
    const range = rangeFrom(from, to, minimum);
    if (range !== null) onChange(range);
  };

  const onCellDown = (day: CalendarDate): void => {
    // A second click after a complete range starts a new one rather than
    // extending it — extending would make an accidental click unrecoverable.
    if (dragFrom.current === null) {
      dragFrom.current = day;
      commit(day, day);
      return;
    }
    commit(dragFrom.current, day);
    dragFrom.current = null;
  };

  const onCellEnter = (day: CalendarDate): void => {
    const anchor = dragFrom.current;
    if (anchor === null) return;
    if (!anchor.equals(day)) swept.current = true;
    commit(anchor, day);
  };

  /**
   * Ends a *drag*, not a two-click selection.
   *
   * A release that never swept across another cell is the first half of a
   * two-click pick, so the pending anchor has to survive it.
   */
  const endDrag = (): void => {
    if (!swept.current) return;
    dragFrom.current = null;
    swept.current = false;
  };

  /**
   * Leaving the card abandons a *drag*, and only a drag.
   *
   * It used to clear the pending anchor too, which quietly forbade the most
   * ordinary long request there is: pick the first day, page to the next month,
   * pick the last. Between those two clicks the pointer leaves the card — to
   * reach the arrow, to read the panel beside it, or just on the way — and the
   * anchor went with it, so the second click started a new range and the pick
   * collapsed to a single day in the second month.
   *
   * A held-down sweep really is abandoned by leaving; a half-finished two-click
   * pick is not a gesture in progress, it is a decision waiting to be finished.
   * `swept` is the same flag `endDrag` uses to tell them apart.
   */
  const abandon = (): void => {
    if (!swept.current) return;
    dragFrom.current = null;
    swept.current = false;
  };

  /**
   * Las flechas mueven el foco; Enter y espacio marcan.
   *
   * Cruzar un mes cambia el mes visible, que es lo que hace utilizable una
   * selección larga sin ratón: se llega al 31 y una flecha más lleva al 1 del
   * siguiente. Se frena en `minimum` porque un día que no se puede reservar
   * tampoco se puede enfocar — llegar a él y que Enter no haga nada sería la
   * misma promesa falsa que esto viene a arreglar.
   */
  const onGridKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const salto: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
      PageUp: -28,
      PageDown: 28,
    };

    const paso = salto[event.key];
    if (paso === undefined) return;

    const desde = focusedRef.current ?? value?.start ?? minimum;
    const destino = desde.addDays(paso);
    /* Nunca antes del primero reservable: un día que no se puede reservar tampoco
       se puede enfocar. */
    const siguiente = destino.isBefore(minimum) ? minimum : destino;

    event.preventDefault();
    focusedRef.current = siguiente;
    setFocusedDay(siguiente);
    if (siguiente.month !== anchor.month || siguiente.year !== anchor.year) {
      setAnchor(siguiente);
    }
  };

  /*
   * Si la selección llega de fuera y cae en otro mes, el calendario va a ella.
   *
   * Los atajos —«Dos semanas», «Una semana», «Un día»— arrancan en el primer día
   * que respeta el aviso previo, que con quince días de aviso casi siempre cae en
   * el mes siguiente. Sin esto se marcaba un rango invisible: el calendario seguía
   * en el mes actual, sin un solo día resaltado, y el atajo parecía no hacer nada.
   *
   * Sólo cuando el rango cambia de verdad, y no en cada render: quien pagina a
   * mano para mirar otro mes no debe verse arrastrado de vuelta.
   */
  const ultimoInicio = useRef<string | null>(null);
  useEffect(() => {
    const inicio = value?.start;
    if (inicio === undefined) return;
    const iso = inicio.toISO();
    if (ultimoInicio.current === iso) return;
    ultimoInicio.current = iso;
    if (inicio.month !== anchor.month || inicio.year !== anchor.year) setAnchor(inicio);
  }, [value, anchor]);

  /*
   * Mover el foco de verdad, después de pintar.
   *
   * `tabIndex` decide cuál celda es la parada, pero el navegador no mueve el foco
   * por su cuenta: sin esto, las flechas cambiarían la apariencia y el teclado
   * seguiría escribiendo en la celda anterior.
   */
  useEffect(() => {
    if (focusedDay === null) return;
    const celda = gridRef.current?.querySelector<HTMLButtonElement>(
      `[data-day="${focusedDay.toISO()}"]`,
    );
    celda?.focus();
  }, [focusedDay]);

  /** Forgets the pending pick outright — what «Limpiar» means. */
  const reset = (): void => {
    dragFrom.current = null;
    swept.current = false;
  };

  const selection = value;
  const hint =
    selection === null
      ? 'Toca el primer día y luego el último'
      : selection.naturalDays === 1
        ? '1 día natural seleccionado'
        : `${String(selection.naturalDays)} días naturales seleccionados`;

  return (
    <div
      className={styles.card}
      onPointerUp={endDrag}
      onPointerLeave={abandon}
    >
      <div className={styles.head}>
        <button
          type="button"
          className={styles.navButton}
          aria-label="Mes anterior"
          onClick={() => {
            setAnchor((current) => current.addMonths(-1));
          }}
        >
          ‹
        </button>
        <div className={styles.monthLabel}>
          {MONTHS_LONG[anchor.month - 1]} {anchor.year}
        </div>
        <button
          type="button"
          className={styles.navButton}
          aria-label="Mes siguiente"
          onClick={() => {
            setAnchor((current) => current.addMonths(1));
          }}
        >
          ›
        </button>
      </div>

      <div className={styles.weekdays}>
        {WEEKDAYS_SHORT.map((label) => (
          <div key={label} className={styles.weekday}>
            {label}
          </div>
        ))}
      </div>

      <div
        className={styles.grid}
        ref={gridRef}
        role="grid"
        aria-label="Días del mes"
        onKeyDown={onGridKeyDown}
      >
        {Array.from({ length: leadingBlanks }, (_, i) => (
          <span key={`blank-${String(i)}`} className={cx(styles.day, styles.dayOutside)} />
        ))}

        {days.map((day) => {
          const iso = day.toISO();
          const isHoliday = holidays.has(iso);
          const disabled = day.isBefore(minimum);
          const inRange = selection !== null && selection.contains(day);
          const isEdge =
            selection !== null && (selection.start.equals(day) || selection.end.equals(day));
          const busyWho = busy.get(iso);

          return (
            <button
              key={iso}
              type="button"
              data-day={iso}
              /*
               * Una sola parada de tabulador para toda la rejilla: la del día que
               * lleva el foco, o el primero reservable mientras nadie use las
               * flechas. Ver `focusedDay`.
               */
              tabIndex={
                (focusedDay ?? selection?.start ?? minimum).equals(day) && !disabled ? 0 : -1
              }
              disabled={disabled}
              className={cx(
                styles.day,
                (day.isWeekend || isHoliday) && styles.dayMuted,
                isHoliday && styles.dayHoliday,
                inRange && styles.dayInRange,
                isEdge && styles.dayEdge,
              )}
              aria-label={`${String(day.day)} de ${MONTHS_LONG[day.month - 1] ?? ''}${
                isHoliday ? ', feriado' : ''
              }${busyWho === undefined ? '' : `, ${busyWho} está fuera`}`}
              aria-pressed={inRange}
              onPointerDown={() => {
                swept.current = false;
                onCellDown(day);
              }}
              /*
               * Enter y espacio, que es lo que el texto de arriba lleva
               * prometiendo desde siempre y no cumplía.
               *
               * Estas celdas ya eran `<button>`, así que Tab siempre funcionó —
               * pero elegir el día colgaba de `onPointerDown`, y un evento de
               * puntero **no lo dispara el teclado**. Se llegaba al día, se
               * pulsaba Enter y no pasaba nada: la promesa era falsa y sólo se
               * notaba usándola sin ratón.
               *
               * `detail === 0` distingue la activación por teclado de la del
               * ratón, que ya pasó por `onPointerDown`. Sin eso, un clic contaría
               * dos veces y el segundo cerraría el rango donde empezó.
               */
              onClick={(event) => {
                if (event.detail !== 0) return;
                swept.current = false;
                onCellDown(day);
              }}
              onPointerEnter={(event) => {
                /*
                 * Only while the button is actually held.
                 *
                 * Without this check, merely *moving* the pointer across the
                 * grid extended the range — after the first click of a two-click
                 * pick, the selection followed the mouse to wherever it happened
                 * to travel on its way out. Picking the 4th and then reaching for
                 * anything else left you with the 4th to the 12th, and it also
                 * marked the gesture as a sweep, which is what threw the pending
                 * anchor away when the pointer left the card.
                 *
                 * `buttons === 1` is the primary button being down right now,
                 * which is the only thing that makes this a drag.
                 */
                if (event.buttons !== 1) return;
                onCellEnter(day);
              }}
            >
              {day.day}
              {busyWho !== undefined && <span className={styles.busyDot} title={busyWho} />}
            </button>
          );
        })}
      </div>

      <div className={styles.foot}>
        <span className={styles.hint}>{hint}</span>
        <button
          type="button"
          className={styles.clear}
          onClick={() => {
            onChange(null);
            reset();
          }}
        >
          Limpiar
        </button>
      </div>
    </div>
  );
};
